// Dashboard.js
import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import BoxesGrid from './BoxesGrid';
import SensorDetailPage from './SensorDetailPage';
import CompositeGraphRouter from './CompositeGraphRouter';
import SensorTimeSeriesTable from './SensorTimeSeriesTable';
import Header from './Header';

export default function Dashboard({ user, onSignOut }) {
  const [currentView, setCurrentView] = useState('boxes');
  const [selectedBox, setSelectedBox] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [selectedSensorData, setSelectedSensorData] = useState(null);

  const handleBoxClick = (box) => {
    console.log('📦 Box selected:', box);
    setSelectedBox(box);
    setCurrentView('sensors');
  };

  const handleBack = () => {
    if (currentView === 'composite' || currentView === 'timeseries') {
      setCurrentView('sensors');
      setSelectedSensor(null);
      setSelectedSensorData(null);
    } else {
      setCurrentView('boxes');
      setSelectedBox(null);
    }
  };

  const handleCompositeGraphClick = (sensorName, sensorData) => {
    console.log('📊 Composite graph clicked:', { sensorName, sensorData });
    if (!sensorName) return;
    setSelectedSensor(sensorName);
    setSelectedSensorData(sensorData);
    setCurrentView('composite');
  };

  const handleTimeSeriesClick = (sensorName, sensorData) => {
    console.log('📈 Time series clicked:', { sensorName, sensorData });
    setSelectedSensor(sensorName);
    setSelectedSensorData(sensorData);
    setCurrentView('timeseries');
  };

  const getBoxId = () => {
    if (!selectedBox) return null;
    return (
      selectedBox.id ||
      selectedBox._id ||
      selectedBox.box_id ||
      selectedBox.boxId ||
      selectedBox.deviceId ||
      selectedBox.device_id ||
      null
    );
  };

  const boxId = getBoxId();

  const canRenderCompositeGraph = () => {
    return !!(boxId && selectedSensor);
  };

  const renderCompositeGraph = () => {
    if (!canRenderCompositeGraph()) {
      return (
        <Container>
          <Alert severity="error" sx={{ mt: 2 }}>
            Error: Missing required data for composite graph.
            <br />
            Box ID: {boxId || 'Missing'}
            <br />
            Sensor: {selectedSensor || 'Missing'}
          </Alert>
        </Container>
      );
    }

    return (
      <CompositeGraphRouter
        boxId={boxId}
        sensorName={selectedSensor}
        sensorData={selectedSensorData}
        onBack={handleBack}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      {/* Fixed Header with Logout */}
      <Header user={user} onSignOut={onSignOut} />

      {/* Main scrollable content */}
      <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
          {currentView === 'boxes' && <BoxesGrid onBoxClick={handleBoxClick} />}

          {currentView === 'sensors' && (
            <SensorDetailPage
              selectedBox={selectedBox}
              onBack={handleBack}
              onCompositeGraphClick={handleCompositeGraphClick}
              onTimeSeriesClick={handleTimeSeriesClick}
            />
          )}

          {currentView === 'composite' && renderCompositeGraph()}

          {currentView === 'timeseries' && (
            <SensorTimeSeriesTable
              boxId={boxId}
              sensorName={selectedSensor}
              sensorData={selectedSensorData}
              onBack={handleBack}
            />
          )}
        </Container>
      </Box>
    </Box>
  );
}